import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { UserProfile, UserService } from '../pages/service/user.service';
import { ProductService } from '../pages/service/product.service';
import { InMemoryStorageService } from '../services/in-memory-storage.service';
import { LoaderService } from '../services/loader.service';
import { environment } from '../../environments/environment';
import { getWarehouseQuickSwitchPresets, WarehouseQuickSwitchPreset } from '../config/warehouse-quick-switch-presets';

@Component({
    selector: 'app-view-profile',
    standalone: true,
    templateUrl: './view-profile.component.html',
    styleUrls: ['./view-profile.component.scss'],
    providers: [MessageService],
    imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, TableModule, ToastModule, ToolbarModule]
})
export class ViewProfileComponent implements OnInit, OnDestroy {
    userProfile: UserProfile | null = null;
    editProfile: Partial<UserProfile> = {};
    error: string | null = null;
    editMode = false;
    saving = false;
    loading = false;
    warehouseSwitchBusy = false;
    selectedWarehouseId: string | null = null;
    private readonly destroy$ = new Subject<void>();
    private readonly defaultCompany = environment.production ? '080' : '782';
    private readonly skipReloadPromptKey = 'skipReloadPrompt';

    readonly warehousePresets: WarehouseQuickSwitchPreset[] = getWarehouseQuickSwitchPresets(this.defaultCompany);

    readonly profileRows = [
        { key: 'userName', label: 'User Name', icon: 'pi pi-user' },
        { key: 'firstName', label: 'First Name', icon: 'pi pi-id-card' },
        { key: 'middleName', label: 'Middle Name', icon: 'pi pi-id-card' },
        { key: 'lastName', label: 'Last Name', icon: 'pi pi-id-card' },
        { key: 'email', label: 'Email', icon: 'pi pi-envelope' },
        { key: 'mobile', label: 'Mobile', icon: 'pi pi-phone' },
        { key: 'company', label: 'Company', icon: 'pi pi-briefcase' },
        { key: 'division', label: 'Division', icon: 'pi pi-sitemap' },
        { key: 'facility', label: 'Facility', icon: 'pi pi-building' },
        { key: 'warehouse', label: 'Warehouse', icon: 'pi pi-warehouse' },
        { key: 'm3ResponsibleId', label: 'M3 Responsible ID', icon: 'pi pi-user' },
        { key: 'roles', label: 'Roles', icon: 'pi pi-users' },
        { key: 'status', label: 'Status', icon: 'pi pi-check-circle' },
        { key: 'createdAt', label: 'Created At', icon: 'pi pi-calendar' }
    ];

    constructor(
        private readonly userService: UserService,
        private readonly messageService: MessageService,
        private readonly productService: ProductService,
        private readonly inMemoryStorageService: InMemoryStorageService,
        private readonly router: Router,
        private readonly loaderService: LoaderService
    ) {}

    ngOnInit(): void {
        this.getUserProfile();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goBack(): void {
        this.router.navigate(['/Orders1']);
    }

    startEdit(): void {
        if (!this.userProfile) {
            return;
        }
        this.editMode = true;
        this.editProfile = {
            mobile: this.userProfile.mobile,
            company: this.userProfile.company,
            division: this.userProfile.division,
            facility: this.userProfile.facility,
            warehouse: this.userProfile.warehouse,
            m3ResponsibleId: this.userProfile.m3ResponsibleId
        };
        this.selectedWarehouseId = this.detectPreset(this.editProfile);
    }

    cancelEdit(): void {
        this.editMode = false;
        this.editProfile = {};
        this.selectedWarehouseId = this.userProfile ? this.detectPreset(this.userProfile) : null;
    }

    /**
     * Immediate save + full refresh (same behavior as topbar quick switch).
     * Save in the toolbar remains for manual edits only.
     */
    switchWarehousePreset(preset: WarehouseQuickSwitchPreset): void {
        if (!this.userProfile || this.warehouseSwitchBusy || this.saving) {
            return;
        }

        this.warehouseSwitchBusy = true;
        this.loaderService.show('Switching warehouse...', 'Applying company / division / facility / warehouse');

        const base = this.userProfile;
        const updatedProfile: UserProfile = {
            ...base,
            company: preset.company,
            division: preset.division,
            facility: preset.facility,
            warehouse: preset.warehouse
        };

        this.userService
            .updateUserProfile({
                userName: base.userName,
                mobile: base.mobile,
                facility: preset.facility,
                warehouse: preset.warehouse,
                m3ResponsibleId: base.m3ResponsibleId,
                division: preset.division,
                company: preset.company
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.userProfile = updatedProfile;
                    this.syncContextAfterSave(updatedProfile);
                    this.selectedWarehouseId = preset.id;
                    this.warehouseSwitchBusy = false;
                    this.loaderService.hide();
                    this.editMode = false;
                    this.editProfile = {};
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `Switched to warehouse ${preset.id}. Reloading…`,
                        life: 2000
                    });
                    sessionStorage.setItem(this.skipReloadPromptKey, 'true');
                    window.location.reload();
                },
                error: (err) => {
                    const backendMessage = typeof err?.error === 'string' ? err.error : err?.error?.message;
                    const errorDetail = backendMessage || 'Failed to switch warehouse. Please try again.';
                    this.warehouseSwitchBusy = false;
                    this.loaderService.hide();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Switch Failed',
                        detail: errorDetail,
                        life: 4000
                    });
                }
            });
    }

    saveProfile(): void {
        if (!this.userProfile || this.saving || this.warehouseSwitchBusy || !this.isSaveEnabled()) {
            return;
        }

        const updatedProfile = {
            ...this.userProfile,
            mobile: this.editProfile.mobile?.toString().trim() || '',
            facility: this.editProfile.facility?.toString().trim() || '',
            warehouse: this.editProfile.warehouse?.toString().trim() || '',
            division: this.editProfile.division?.toString().trim() || '',
            company: this.editProfile.company?.toString().trim() || '',
            m3ResponsibleId: this.editProfile.m3ResponsibleId?.toString().trim() || ''
        };

        this.saving = true;
        this.loaderService.show('Saving your profile...', 'Please wait...');

        this.userService
            .updateUserProfile({
                userName: updatedProfile.userName,
                mobile: updatedProfile.mobile,
                facility: updatedProfile.facility,
                warehouse: updatedProfile.warehouse,
                m3ResponsibleId: updatedProfile.m3ResponsibleId,
                division: updatedProfile.division,
                company: updatedProfile.company
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.userProfile = updatedProfile;
                    this.syncContextAfterSave(updatedProfile);
                    this.saving = false;
                    this.loaderService.hide();
                    this.cancelEdit();
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Profile updated successfully.',
                        life: 3000
                    });
                },
                error: (err) => {
                    const backendMessage = typeof err?.error === 'string' ? err.error : err?.error?.message;
                    this.error = backendMessage || 'Failed to update profile. Please try again later.';
                    const errorDetail = this.error ?? 'Failed to update profile. Please try again later.';
                    this.saving = false;
                    this.loaderService.hide();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Update Failed',
                        detail: errorDetail,
                        life: 4000
                    });
                }
            });
    }

    getProfileValue(key: string): string {
        if (!this.userProfile) {
            return '';
        }
        if (key === 'roles' || key === 'roleGroups') {
            return Array.isArray(this.userProfile.roleGroups) ? this.userProfile.roleGroups.join(', ') : '';
        }
        return (this.userProfile as any)[key] ?? '';
    }

    isSaveEnabled(): boolean {
        const required = ['mobile', 'company', 'division', 'facility', 'warehouse', 'm3ResponsibleId'] as const;
        return required.every((field) => !!this.editProfile[field]?.toString().trim());
    }

    private getUserProfile(): void {
        const userName = this.userService.getUserName();
        if (!userName) {
            this.error = 'No username found in current session.';
            return;
        }

        this.loading = true;
        this.loaderService.show('Loading your profile...', 'Please wait...');

        this.userService
            .getUserProfile(userName)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (profile) => {
                    this.userProfile = profile;
                    this.selectedWarehouseId = this.detectPreset(profile);
                    this.loading = false;
                    this.loaderService.hide();
                    this.productService.setCompanyAndDivision(profile.company, profile.division);
                    this.userService.setCompanyAndDivision(profile.company, profile.division);
                },
                error: () => {
                    this.error = 'Failed to load profile.';
                    this.loading = false;
                    this.loaderService.hide();
                }
            });
    }

    private detectPreset(profile: Partial<UserProfile>): string | null {
        const company = profile.company?.toString().trim();
        const division = profile.division?.toString().trim();
        const facility = profile.facility?.toString().trim();
        const warehouse = profile.warehouse?.toString().trim();

        const matched = this.warehousePresets.find(
            (preset) =>
                preset.company === company && preset.division === division && preset.facility === facility && preset.warehouse === warehouse
        );
        return matched?.id ?? null;
    }

    private syncContextAfterSave(profile: UserProfile): void {
        this.productService.setCompanyAndDivision(profile.company, profile.division);
        this.userService.setCompanyAndDivision(profile.company, profile.division);

        this.inMemoryStorageService.set('company', profile.company);
        this.inMemoryStorageService.set('division', profile.division);
        this.inMemoryStorageService.set('companyId', profile.company);
        this.inMemoryStorageService.set('divisionId', profile.division);
        this.inMemoryStorageService.set('facilityId', profile.facility);
        this.inMemoryStorageService.set('warehouseId', profile.warehouse);
        this.inMemoryStorageService.set('m3ResponsibleId', profile.m3ResponsibleId);

        const rawUserProfile = localStorage.getItem('userProfile');
        if (rawUserProfile) {
            try {
                const parsed = JSON.parse(rawUserProfile);
                parsed.company = profile.company;
                parsed.division = profile.division;
                parsed.facility = profile.facility;
                parsed.warehouse = profile.warehouse;
                localStorage.setItem('userProfile', JSON.stringify(parsed));
            } catch {
                // Ignore parse issues and keep runtime state updated.
            }
        }
    }
}